import { useCallback, useEffect, useState } from "react";
import { ShieldOff, Unlock, Loader2, RefreshCw, Megaphone, MegaphoneOff } from "lucide-react";
import { api } from "@/lib/api";
import { authHeader } from "@/lib/adminAuth";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export const BotControlsAdminCard = ({ onUnauthorized }) => {
  const [warnOn, setWarnOn] = useState(null);
  const [tgPaused, setTgPaused] = useState(null);
  const [tgBusy, setTgBusy] = useState(false);
  const [quotesOn, setQuotesOn] = useState(null);
  const [quotesBusy, setQuotesBusy] = useState(false);
  const [paused, setPaused] = useState([]);
  const [rocketPaused, setRocketPaused] = useState([]);
  const [guards, setGuards] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, p, r] = await Promise.all([
        api.get("/platform-settings"),
        api.get("/admin/over3-paused", { headers: authHeader() }),
        api.get("/admin/rocket-paused", { headers: authHeader() }),
      ]);
      setWarnOn(s.data?.overtrade_warning !== false);
      setPaused(p.data?.paused || []);
      setRocketPaused(r.data?.paused || []);
    } catch (e) {
      if (e?.response?.status === 401 && onUnauthorized) onUnauthorized();
    }
    try {
      const g = await api.get("/admin/rocket-guard", { headers: authHeader() });
      setGuards(g.data?.guards || []);
    } catch { /* section renders empty */ }
    try {
      const t = await api.get("/admin/telegram/alerts-pause", { headers: authHeader() });
      setTgPaused(!!t.data?.paused);
    } catch { /* section renders with unknown state */ }
    try {
      const q = await api.get("/admin/telegram/quotes", { headers: authHeader() });
      setQuotesOn(!!q.data?.enabled);
    } catch { /* section renders with unknown state */ }
  }, [onUnauthorized]);
  useEffect(() => { load(); }, [load]);

  const toggleWarn = async (v) => {
    setBusy(true);
    try {
      await api.post("/admin/platform-settings", { overtrade_warning: v }, { headers: authHeader() });
      setWarnOn(v);
      toast.success(v ? "Overtrade warning ON — users see the nudge again" : "Overtrade warning OFF — users trade freely");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleTg = async (alertsOn) => {
    setTgBusy(true);
    try {
      await api.post(`/admin/telegram/alerts-pause?paused=${!alertsOn}`, {}, { headers: authHeader() });
      setTgPaused(!alertsOn);
      toast.success(alertsOn
        ? "Group alerts RESUMED — wins post to Telegram again"
        : "Group alerts PAUSED — no bot posts anything to the group until you resume");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setTgBusy(false);
    }
  };

  const toggleQuotes = async (v) => {
    setQuotesBusy(true);
    try {
      await api.post(`/admin/telegram/quotes?enabled=${v}`, {}, { headers: authHeader() });
      setQuotesOn(v);
      toast.success(v ? "Daily quote posts ON — one AI post per day at 10:00 UTC" : "Daily quote posts OFF — no more hype/quote messages");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setQuotesBusy(false);
    }
  };

  const unpause = async (accountId) => {
    try {
      await api.post(`/admin/over3-unpause/${encodeURIComponent(accountId)}`, {}, { headers: authHeader() });
      setPaused((cur) => cur.filter((p) => p.account_id !== accountId));
      toast.success(`${accountId} un-paused — they can start the bot now`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Un-pause failed");
    }
  };

  const unpauseRocket = async (accountId) => {
    try {
      await api.post(`/admin/rocket-unpause/${encodeURIComponent(accountId)}`, {}, { headers: authHeader() });
      setRocketPaused((cur) => cur.filter((p) => p.account_id !== accountId));
      toast.success(`${accountId} rocket un-parked — they can fly again`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Un-park failed");
    }
  };

  const overrideGuard = async (accountId) => {
    try {
      await api.post(`/admin/rocket-guard-override/${encodeURIComponent(accountId)}`, {}, { headers: authHeader() });
      setGuards((cur) => cur.map((g) => (g.account_id === accountId ? { ...g, tripped: false, override: true } : g)));
      toast.success(`${accountId} circuit-breaker lifted for today`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Override failed");
    }
  };

  if (warnOn === null) return null;
  return (
    <div data-testid="bot-controls-admin-card" className="vts-card p-4 mt-6 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <ShieldOff className="h-4 w-4 text-vts-blue" />
        <span className="text-sm font-semibold text-white">Bot friction controls</span>
        <span className="text-[10px] text-vts-silver/55 font-mono">every blocked session is lost markup — you decide the guardrails</span>
        <button data-testid="bot-controls-refresh" onClick={load} className="ml-auto text-vts-silver/50 hover:text-white">
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 ${tgPaused
        ? "border-vts-loss/40 bg-vts-loss/5" : "border-vts-line bg-vts-surface/40"}`}>
        {tgPaused ? <MegaphoneOff className="h-4 w-4 text-vts-loss shrink-0" /> : <Megaphone className="h-4 w-4 text-vts-profit shrink-0" />}
        <div className="flex-1">
          <div className="text-xs font-semibold text-white">
            Telegram group alerts — ALL bots
            {tgPaused !== null && (
              <span className={`ml-2 px-1.5 py-0.5 rounded text-[9px] font-black ${tgPaused
                ? "bg-vts-loss/15 text-vts-loss" : "bg-emerald-500/15 text-emerald-300"}`}>
                {tgPaused ? "PAUSED" : "LIVE"}
              </span>
            )}
          </div>
          <div className="text-[10px] text-vts-silver/55 font-mono">
            one switch mutes every group post (fleet, cards, coin, rocket, arcade, streak brags). Your admin DMs stay live. Survives restarts.
          </div>
        </div>
        {tgBusy || tgPaused === null ? <Loader2 className="h-4 w-4 animate-spin text-vts-silver/50" /> :
          <Switch data-testid="tg-alerts-toggle" checked={!tgPaused} onCheckedChange={toggleTg} />}
      </div>

      <div className="flex items-center gap-3 rounded-lg border border-vts-line bg-vts-surface/40 px-3 py-2.5">
        <div className="flex-1">
          <div className="text-xs font-semibold text-white">
            Daily quote / hype posts
            {quotesOn !== null && (
              <span className={`ml-2 px-1.5 py-0.5 rounded text-[9px] font-black ${quotesOn
                ? "bg-emerald-500/15 text-emerald-300" : "bg-vts-loss/15 text-vts-loss"}`}>
                {quotesOn ? "ON" : "OFF"}
              </span>
            )}
          </div>
          <div className="text-[10px] text-vts-silver/55 font-mono">
            the AI-written daily post to the group. OFF by default — nothing sends until you flip it on. The master pause above also silences it.
          </div>
        </div>
        {quotesBusy || quotesOn === null ? <Loader2 className="h-4 w-4 animate-spin text-vts-silver/50" /> :
          <Switch data-testid="tg-quotes-toggle" checked={quotesOn} onCheckedChange={toggleQuotes} />}
      </div>

      <div className="flex items-center gap-3 rounded-lg border border-vts-line bg-vts-surface/40 px-3 py-2.5">
        <div className="flex-1">
          <div className="text-xs font-semibold text-white">"Whoa — running it again?" popup</div>
          <div className="text-[10px] text-vts-silver/55 font-mono">nudges users to stop after 2+ digit-bot sessions a day. OFF = never shown, platform-wide.</div>
        </div>
        {busy ? <Loader2 className="h-4 w-4 animate-spin text-vts-silver/50" /> :
          <Switch data-testid="overtrade-warning-toggle" checked={warnOn} onCheckedChange={toggleWarn} />}
      </div>

      <div className="rounded-lg border border-vts-line bg-vts-surface/40 px-3 py-2.5 space-y-1.5">
        <div className="text-xs font-semibold text-white">Paused accounts ("stop for today")</div>
        {paused.length === 0 ? (
          <div className="text-[10px] text-vts-silver/45 font-mono">none — nobody is locked out right now</div>
        ) : paused.map((p) => (
          <div key={p.account_id} data-testid={`paused-row-${p.account_id}`} className="flex items-center gap-2 text-[11px] font-mono">
            <span className="text-white">{p.account_id}</span>
            <span className="text-vts-silver/50">until {String(p.paused_until).slice(11, 16)} UTC</span>
            <button data-testid={`unpause-btn-${p.account_id}`} onClick={() => unpause(p.account_id)}
              className="ml-auto flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[10px] font-bold text-emerald-300 hover:bg-emerald-500/20">
              <Unlock className="h-3 w-3" /> Un-pause
            </button>
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-vts-line bg-vts-surface/40 px-3 py-2.5 space-y-1.5">
        <div className="text-xs font-semibold text-white">🚀 Rocket parks (manual + 24/7 bot)</div>
        {rocketPaused.length === 0 ? (
          <div className="text-[10px] text-vts-silver/45 font-mono">none — no rockets parked right now</div>
        ) : rocketPaused.map((p) => (
          <div key={p.account_id} data-testid={`rocket-paused-row-${p.account_id}`} className="flex items-center gap-2 text-[11px] font-mono">
            <span className="text-white">{p.account_id}</span>
            <span className="text-vts-silver/50">until {String(p.paused_until).slice(11, 16)} UTC</span>
            <button data-testid={`rocket-unpause-btn-${p.account_id}`} onClick={() => unpauseRocket(p.account_id)}
              className="ml-auto flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[10px] font-bold text-emerald-300 hover:bg-emerald-500/20">
              <Unlock className="h-3 w-3" /> Un-park
            </button>
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-vts-line bg-vts-surface/40 px-3 py-2.5 space-y-1.5">
        <div className="text-xs font-semibold text-white">🚨 Rocket daily circuit-breakers (60% cap · local + server flights)</div>
        {guards.length === 0 ? (
          <div className="text-[10px] text-vts-silver/45 font-mono">none — no real account has flown today</div>
        ) : guards.map((g) => (
          <div key={g.account_id} data-testid={`rocket-guard-row-${g.account_id}`} className="flex items-center gap-2 text-[11px] font-mono">
            <span className="text-white">{g.account_id}</span>
            <span className={g.net < 0 ? "text-vts-loss" : "text-vts-profit"}>{g.net >= 0 ? "+" : ""}{Number(g.net).toFixed(2)}</span>
            <span className="text-vts-silver/50">/ cap −{Number(g.cap || 0).toFixed(2)}</span>
            {g.override && <span className="px-1 py-0.5 rounded bg-emerald-500/15 text-emerald-300 text-[9px] font-bold">OVERRIDDEN</span>}
            {g.tripped && (
              <button data-testid={`rocket-guard-override-btn-${g.account_id}`} onClick={() => overrideGuard(g.account_id)}
                className="ml-auto flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[10px] font-bold text-emerald-300 hover:bg-emerald-500/20">
                <Unlock className="h-3 w-3" /> Lift breaker
              </button>
            )}
            {!g.tripped && <span className="ml-auto text-[9px] text-vts-silver/40">{g.override ? "" : "flying free"}</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default BotControlsAdminCard;
